import React from 'react';
import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Trophy, Users } from 'phosphor-react';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import { useFinanceiroClientesRanking } from '../../../../hooks/useFinanceiro';
import { FinanceiroPageHeader } from '../components/FinanceiroPageHeader';
import { brl } from '../components/format';
import { InfoPopover } from '../components/InfoPopover';
import { KpiCard } from '../components/KpiCard';
import { useFinanceiroFilters } from '../components/useFinanceiroFilters';

const fmtPct = (v: number) => `${v.toFixed(1).replace('.', ',')}%`;

export const FinanceiroClientesPage = () => {
  const f = useFinanceiroFilters();
  const { data, isError } = useFinanceiroClientesRanking({ ...f.apiParams });

  const itens = data?.itens ?? [];
  const totalValor = data?.total_valor ?? 0;
  const max = Math.max(1, ...itens.map(i => i.total));

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <FinanceiroPageHeader
          eyebrow="Comercial"
          titulo="Ranking de clientes"
          subtitulo="Clientes que mais faturam no período (por vendas)"
          icon={<Trophy size={24} weight="fill" />}
          cor="#CA8A04"
          info={
            <Text>
              Top 30 clientes por faturamento no período/franquia, com nº de vendas e{' '}
              <b>% do total</b>. A base é <i>ca_vendas</i>.
            </Text>
          }
        />

        {isError && (
          <GlassCard intensity="medium" p={6} mb={5} tint="#DC2626">
            <Text color="#71717A">Não foi possível carregar o ranking.</Text>
          </GlassCard>
        )}

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
          <KpiCard
            label="Faturamento (top)"
            valor={totalValor}
            color="#CA8A04"
            icon={<Trophy size={20} weight="duotone" />}
            index={0}
            info={
              <InfoPopover title="Faturamento do ranking">
                Soma do faturamento dos clientes listados (top 30) no período/franquia.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Clientes"
            valor={itens.length}
            format="number"
            color="#2563EB"
            icon={<Users size={20} weight="duotone" />}
            index={1}
            info={
              <InfoPopover title="Clientes">
                Clientes distintos com vendas no período (top 30).
              </InfoPopover>
            }
          />
        </SimpleGrid>

        <GlassCard intensity="strong" p={{ base: 3, md: 5 }}>
          {itens.length === 0 ? (
            <Text fontSize="sm" color="#94A3B8" textAlign="center" py={6}>
              Sem vendas no período.
            </Text>
          ) : (
            <Flex direction="column" gap={3}>
              {itens.map((c, i) => (
                <motion.div
                  key={c.cliente_nome ?? 'sem-nome'}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.25, delay: 0.02 * i }}
                >
                  <Box>
                    <Flex justify="space-between" align="center" gap={2} mb={1}>
                      <Flex align="center" gap={2} minW={0}>
                        <Text
                          fontSize="xs"
                          fontWeight={700}
                          color="#94A3B8"
                          w="22px"
                          flexShrink={0}
                        >
                          {i + 1}º
                        </Text>
                        <Text fontSize="sm" fontWeight={600} color="#0F172A" noOfLines={1}>
                          {c.cliente_nome || '—'}
                        </Text>
                      </Flex>
                      <Flex align="center" gap={3} whiteSpace="nowrap">
                        <Text fontSize="11px" color="#94A3B8">
                          {c.qtd} {c.qtd === 1 ? 'venda' : 'vendas'} ·{' '}
                          {fmtPct((c.total / Math.max(1, totalValor)) * 100)}
                        </Text>
                        <Text
                          fontSize="sm"
                          fontWeight={700}
                          color="#0F172A"
                          w="110px"
                          textAlign="right"
                        >
                          {brl(c.total)}
                        </Text>
                      </Flex>
                    </Flex>
                    <Box
                      bg="rgba(15,23,42,0.05)"
                      borderRadius="full"
                      h="6px"
                      overflow="hidden"
                      ml="30px"
                    >
                      <Box
                        h="full"
                        w={`${Math.max(2, (c.total / max) * 100)}%`}
                        bg="#CA8A04"
                        borderRadius="full"
                      />
                    </Box>
                  </Box>
                </motion.div>
              ))}
            </Flex>
          )}
        </GlassCard>
      </Box>
    </GlassPageBg>
  );
};
